import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { MapPin, Plus, TrendingUp, TrendingDown, Loader2, Clock, CheckCircle2, Calendar } from "lucide-react";

/**
 * AdminSourcingTrips — list all sourcing trips with roll-up P&L per trip.
 *
 * Only ONE trip can be open at a time (backend enforces via update_many).
 * The header shows a "start a new trip" affordance; picking a label starts
 * a trip and auto-links every subsequently-created inventory item to it
 * until she closes the trip.
 */
export default function AdminSourcingTrips() {
  const [trips, setTrips] = useState(null);
  const [starting, setStarting] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/sourcing-trips");
      setTrips(data.trips || []);
    } catch (e) {
      toast.error(`could not load trips: ${e.response?.data?.detail || e.message}`);
      setTrips([]);
    }
  };
  useEffect(() => { load(); }, []);

  const startTrip = async () => {
    const label = window.prompt("what should we call this trip?", `${new Date().toLocaleDateString()} · sourcing`);
    if (!label) return;
    setStarting(true);
    try {
      const { data } = await api.post("/sourcing-trips", { label, notes: "" });
      toast.success(`trip started — every new piece today auto-links to "${data.label}".`);
      await load();
      nav(`/admin/sourcing-trips/${data.id}`);
    } catch (e) {
      toast.error(`could not start trip: ${e.response?.data?.detail || e.message}`);
    } finally {
      setStarting(false);
    }
  };

  const openTrip = trips?.find((t) => t.is_open);

  return (
    <AdminLayout>
      <div className="p-6 sm:p-10 space-y-6 max-w-7xl">
        <header className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="editorial-label mb-1">field · sourcing</p>
            <h1 className="font-serif text-4xl text-charcoal">sourcing trips</h1>
            <p className="text-espresso mt-2 max-w-3xl text-sm">
              group each thrift-run into one trip. every piece you add while a trip is open auto-links to it,
              so you can see later which trips actually paid for themselves.
            </p>
          </div>
          <button
            onClick={startTrip}
            disabled={starting}
            className="border border-brass bg-brass text-ivory hover:bg-charcoal hover:border-charcoal disabled:opacity-40 px-4 py-2 text-[0.65rem] tracking-widest uppercase inline-flex items-center gap-2"
            data-testid="start-trip-btn"
          >
            {starting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />} start new trip
          </button>
        </header>

        {openTrip && (
          <div
            className="border border-emerald-500/60 bg-emerald-50 p-4 flex items-start gap-3"
            data-testid="open-trip-banner"
          >
            <Clock className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-charcoal">
                <strong>{openTrip.label}</strong> is currently open — new pieces link here automatically.
              </p>
              <p className="text-xs text-taupe mt-1">
                started {new Date(openTrip.started_at).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })} · {openTrip.items_count} piece{openTrip.items_count === 1 ? "" : "s"} so far
              </p>
            </div>
            <button
              onClick={() => nav(`/admin/sourcing-trips/${openTrip.id}`)}
              className="border border-emerald-700 text-emerald-700 hover:bg-emerald-700 hover:text-ivory px-3 py-1.5 text-[0.6rem] tracking-widest uppercase"
              data-testid="open-current-trip"
            >
              view trip →
            </button>
          </div>
        )}

        {trips === null && <p className="text-taupe text-sm"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> loading…</p>}
        {trips && trips.length === 0 && (
          <div className="border border-taupe/40 p-8 text-center text-taupe text-sm" data-testid="trips-empty">
            no trips yet. tap <em>start new trip</em> at the top before your next thrift run.
          </div>
        )}

        {trips && trips.length > 0 && (
          <div className="border border-taupe/40 bg-ivory overflow-x-auto">
            <table className="w-full text-xs" data-testid="trips-table">
              <thead className="bg-linen border-b border-taupe/40">
                <tr className="text-[0.6rem] tracking-widest uppercase text-taupe">
                  <th className="px-4 py-3 text-left">trip</th>
                  <th className="px-3 py-3 text-right">date</th>
                  <th className="px-3 py-3 text-right">pieces</th>
                  <th className="px-3 py-3 text-right">sold</th>
                  <th className="px-3 py-3 text-right">spent</th>
                  <th className="px-3 py-3 text-right">revenue</th>
                  <th className="px-3 py-3 text-right">net</th>
                  <th className="px-3 py-3 text-right">ROI</th>
                </tr>
              </thead>
              <tbody>
                {trips.map((t) => (
                  <tr
                    key={t.id}
                    onClick={() => nav(`/admin/sourcing-trips/${t.id}`)}
                    className="border-b border-taupe/20 hover:bg-linen/40 cursor-pointer"
                    data-testid={`trip-row-${t.id}`}
                  >
                    <td className="px-4 py-2.5 text-charcoal">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-3 h-3 text-brass" />
                        <span className="font-medium">{t.label}</span>
                        {t.is_open && <span className="text-[0.55rem] tracking-widest uppercase text-emerald-800 bg-emerald-100 px-1.5 py-0.5">open</span>}
                      </div>
                      {t.vendors.length > 0 && (
                        <p className="text-taupe text-[0.65rem] mt-1">{t.vendors.slice(0, 3).join(" · ")}{t.vendors.length > 3 ? ` · +${t.vendors.length - 3}` : ""}</p>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-right text-taupe">
                      {new Date(t.started_at).toLocaleDateString([], { month: "short", day: "numeric" })}
                    </td>
                    <td className="px-3 py-2.5 text-right">{t.items_count}</td>
                    <td className="px-3 py-2.5 text-right">{t.sold_count}</td>
                    <td className="px-3 py-2.5 text-right text-taupe">${t.total_spent.toFixed(2)}</td>
                    <td className="px-3 py-2.5 text-right text-taupe">${t.total_revenue.toFixed(2)}</td>
                    <td className={`px-3 py-2.5 text-right font-medium ${t.net_profit >= 0 ? "text-emerald-800" : "text-red-800"}`}>
                      {t.net_profit >= 0 ? <TrendingUp className="inline w-3 h-3 mr-1" /> : <TrendingDown className="inline w-3 h-3 mr-1" />}
                      ${t.net_profit.toFixed(2)}
                    </td>
                    <td className={`px-3 py-2.5 text-right ${t.roi_pct == null ? "text-taupe" : t.roi_pct >= 0 ? "text-emerald-800" : "text-red-800"}`}>
                      {t.roi_pct == null ? "—" : `${t.roi_pct}%`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
